import {
  ButtonHTMLAttributes,
  Component,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import Feature from 'ol/Feature'
import OlGeolocation from 'ol/Geolocation'
import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Circle from 'ol/style/Circle'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Point from 'ol/geom/Point'
import { Map } from 'ol'
import { Extent, getCenter } from 'ol/extent'
import { transformExtent } from 'ol/proj'
import CompassIcon from '../../images/Compass.svg'

type GeolocationProps = {
  map: Map
}

type RotateControlsProps = {
  map: Map
}

type ZoomToExtentProps = {
  map: Map
  extent: Extent
}

type MapControlsProps = GeolocationProps & {
  container: HTMLElement
  showGeolocationControl?: boolean
  extent?: Extent
  children?: React.ReactNode
}

type GeolocationState = {
  active: boolean
  loading: boolean
}

export default function MapControls({
  map,
  extent,
  container,
  showGeolocationControl = true,
  children,
}: MapControlsProps) {
  return ReactDOM.createPortal(
    <div className="tw-flex tw-flex-col tw-gap-y-4">
      <MapControlToolbar>
        {extent && <ZoomToExtent map={map} extent={extent} />}
        {showGeolocationControl && <Geolocation map={map} />}
      </MapControlToolbar>
      <RotateControls map={map} />
      {children && <MapControlToolbar>{children}</MapControlToolbar>}
    </div>,
    container
  )
}

function ZoomToExtent({ map, extent }: ZoomToExtentProps) {
  return (
    <MapControlButton
      icon="fa-arrows-to-dot"
      title="Zoom to Field Boundary"
      onClick={() =>
        map
          .getView()
          .fit(
            Math.abs(getCenter(extent)[0]) > 180
              ? extent
              : transformExtent(extent, 'EPSG:4326', 'EPSG:3857'),
            {
              duration: 500,
            }
          )
      }
    />
  )
}

class Geolocation extends Component<GeolocationProps, GeolocationState> {
  constructor(props: GeolocationProps) {
    super(props)

    this.state = {
      active: false,
      loading: false,
    }
  }

  geolocation: OlGeolocation | undefined = undefined
  position: Feature<Point> | undefined = undefined
  geolocationLayer:
    | VectorLayer<VectorSource<Feature<Point>>>
    | undefined = undefined

  setup() {
    this.geolocation = new OlGeolocation({
      trackingOptions: { enableHighAccuracy: true },
      projection: this.props.map.getView().getProjection(),
    })

    this.position = new Feature()
    this.position.setStyle(
      new Style({
        image: new Circle({
          radius: 12,
          fill: new Fill({ color: '#3399CC' }),
          stroke: new Stroke({ color: '#fff', width: 3 }),
        }),
      })
    )

    this.geolocation.on('change:position', () => {
      const coordinates = this.geolocation?.getPosition()
      if (coordinates && this.position) {
        this.position.setGeometry(new Point(coordinates))
        this.props.map.getView().animate({ center: coordinates, duration: 500 })
        this.setState({ loading: false })
      }
    })

    this.geolocationLayer = new VectorLayer({
      map: this.props.map,
      source: new VectorSource({ features: [this.position] }),
    })
  }

  componentWillUnmount() {
    if (this.geolocationLayer) {
      this.props.map.removeLayer(this.geolocationLayer)
    }
  }

  toggle(visible: boolean) {
    if (!this.geolocation) {
      this.setup()
    }

    this.geolocation?.setTracking(visible)
    const coordinates = this.geolocation?.getPosition()
    if (coordinates && visible && this.position) {
      this.position.setGeometry(new Point(coordinates))
    }

    const loading = visible && !coordinates
    this.setState({ active: visible, loading: loading })

    if (this.geolocationLayer) {
      this.geolocationLayer.setVisible(visible)
      this.geolocationLayer.changed()
    }
  }

  render() {
    return (
      <MapControlButton
        icon={
          this.state.loading
            ? 'fa-spinner fa-spin'
            : this.state.active
            ? 'fa-location-arrow text-primary'
            : 'fa-location-arrow text-system-gray-400'
        }
        title="Follow your Location"
        onClick={() => this.toggle(!this.state.active)}
      />
    )
  }
}

const rotationStep = (360 / 40 / 180) * Math.PI

function RotateControls({ map }: RotateControlsProps) {
  const [rotation, setRotation] = useState<number>(() =>
    map.getView().getRotation()
  )
  // This is used to tell the difference between updates
  // to the rotation value that is made by this control,
  // compared to updates that happen externally when
  // the map is rotated outside this control.
  // When this control modifies rotation, it will also
  // increment the "current" count of the sync object,
  // so last !== current until the effect below
  // synchronizes the map rotation and also updates
  // the "last" value.
  const sync = useRef({ last: 0, current: 0 })

  const view = map.getView()
  useEffect(() => {
    view.on('change:rotation', onRotate)
    return () => {
      view.un('change:rotation', onRotate)
    }

    function onRotate() {
      setRotation(view.getRotation())
    }
  }, [view])

  useEffect(() => {
    if (sync.current.current !== sync.current.last) {
      map.getView().animate({ rotation, duration: 100 })
      sync.current.last = sync.current.current
    }
  }, [map, rotation])

  return (
    <MapControlToolbar>
      <MapControlButton
        icon="fa-rotate-left"
        title="Rotate left&#010;Shift+Alt+Drag"
        onClick={() => {
          setRotation(rotation => rotation - rotationStep)
          sync.current.current++
        }}
      />
      <button
        title="Reset to North"
        onClick={() => {
          setRotation(0)
          sync.current.current++
        }}>
        <div
          className="tw-origin-center"
          style={{
            width: 32,
            height: 32,
            transform: `translate(-2px,-1px) rotate(${rotation + Math.PI}rad)`,
          }}>
          <CompassIcon />
        </div>
      </button>
      <MapControlButton
        icon="fa-rotate-right"
        title="Rotate right&#010;Shift+Alt+Drag"
        onClick={() => {
          setRotation(rotation => rotation + rotationStep)
          sync.current.current++
        }}
      />
    </MapControlToolbar>
  )
}

export function MapControlToolbar({ children }: { children: ReactNode }) {
  return <div className="map-control-button-group">{children}</div>
}

interface MapControlButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string
  active?: boolean
}

export function MapControlButton({
  icon,
  active,
  disabled,
  ...props
}: MapControlButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled}
      className={active ? 'selected' : disabled ? 'disabled' : ''}>
      <i className={`tw-text-sm fas ${icon}`} />
    </button>
  )
}
